import api from '../../../services/apiConfig';

// --- Interfaces ---

export interface Client {
    ID: number;
    NOME: string;
    FANTASIA: string;
    DOCUMENTO: string;
    TIPOPESSOA: string;
    ENDERECO: string;
    NUMERO: string;
    COMPLEMENTO: string;
    BAIRRO: string;
    CEP: string;
    CIDADE: string;
    ESTADO: string;
    PAIS: string;
    TELEFONE: string;
    DDD: string;
    EMAIL: string;
    SITE: string;
    VENDEDOR: number;
    VENDEDOR_NOME?: string;
    SUPORTE: string;        // <--- Novo (era string no seu código anterior, mas no banco é int)
    SUPORTE_NOME?: string;
    SEGMENTO: number;
    SEGMENTO_NOME?: string;
    ATIVO: string;
    DATA_PEDIDO: string;
    DATACAD: string;
    LIMITE: number;
    RC: string;
    CNPJ_GRUPO: string;
    TIPO: string;
    CLIENTE_GRUPO: string;
    OBS: string;
    IBGE: string;
    BASE_NOME: string;
}

export interface PaginationInfo {
    currentPage: number;
    totalPages: number;
    totalItems: number;
}

export interface ClientsApiResponse {
    clients: Client[];
    pagination: PaginationInfo;
}

export interface ClientOrder {
    ID: number;
    COD_TEOREMA: number;
    EMISSAO: string;
    PREVISAO: string;
    SAIDA: string | null;
    NOTA_FISCAL: string | null;
    SITUACAO: string;
    TIPO_PEDIDO: string;
    VALOR_TOTAL: number;
}

export interface ClientOrdersResponse {
    orders: ClientOrder[];
    pagination: PaginationInfo;
}

export interface ClientStats {
    totalPedidos: number;
    faturamentoTotal: number;
    totalPares: number;
    ticketMedio: number;
}

export interface ClientProduct {
    linha: string;
    item: string;
    totalQuantity: number;
    totalRevenue: number;
}

export interface Segmento {
    ID: number;
    DESCRICAO: string;
}

export interface Estado {
    ESTADO: string;
}

export interface Cidade {
    CIDADE: string;
}

export interface ClientSearchResult {
    ID: number;
    NOME: string;
}

export interface FilterCount {
    ID?: number;
    DESCRICAO?: string;
    ESTADO?: string;
    CIDADE?: string;
    ATIVO?: string;
    count: number;
}

export interface FilterCounts {
    segmentos: FilterCount[];
    estados: FilterCount[];
    cidades: FilterCount[];
    status: FilterCount[];
}

// --- Funções de Fetch ---

/**
 * Lista todos os clientes com paginação e filtros
 * GET /api/clients
 */
export const fetchClients = async (
    page: number = 1,
    limit: number = 30,
    search?: string,
    segmento?: string,
    estado?: string,
    ativo?: string,
    vendedorId?: string,
    ordenacao?: string
) => {
    const params: any = { page, limit };
    if (search) params.search = search;
    if (segmento) params.segmento = segmento;
    if (estado) params.estado = estado;
    if (ativo) params.ativo = ativo;
    if (vendedorId) params.vendedorId = vendedorId;
    if (ordenacao) params.ordenacao = ordenacao;

    return api.get<ClientsApiResponse>('/clients', { params });
};

/**
 * Busca detalhes de um cliente específico
 * GET /api/clients/:id
 */
export const getClientById = async (id: number | string) => {
    return api.get<Client>(`/clients/${id}`);
};

/**
 * Busca pedidos de um cliente específico
 * GET /api/clients/:id/orders
 */
export const getClientOrders = async (
    id: number | string,
    dataI?: string,
    dataF?: string,
    page: number = 1,
    limit: number = 15
) => {
    return api.get<ClientOrdersResponse>(`/clients/${id}/orders`, {
        params: { dataI, dataF, page, limit }
    });
};

/**
 * Busca estatísticas de um cliente específico
 * GET /api/clients/:id/stats
 */
export const getClientStats = async (
    id: number | string,
    dataI?: string,
    dataF?: string
) => {
    return api.get<ClientStats>(`/clients/${id}/stats`, {
        params: { dataI, dataF }
    });
};

/**
 * Busca produtos comprados por um cliente específico
 * GET /api/clients/:id/products
 */
export const getClientProducts = async (
    id: number | string,
    dataI?: string,
    dataF?: string
) => {
    return api.get<ClientProduct[]>(`/clients/${id}/products`, {
        params: { dataI, dataF }
    });
};

/**
 * Busca contadores para todos os filtros
 * GET /api/clients/filters/counts
 */
export const fetchFilterCounts = async (vendedorId?: string) => {
    return api.get<FilterCounts>('/clients/filters/counts', {
        params: { vendedorId }
    });
};

/**
 * Busca lista de segmentos
 * GET /api/clients/filters/segmentos
 */
export const fetchSegmentos = async () => {
    return api.get<Segmento[]>('/clients/filters/segmentos');
};

/**
 * Busca lista de estados
 * GET /api/clients/filters/estados
 */
export const fetchEstados = async () => {
    return api.get<Estado[]>('/clients/filters/estados');
};

/**
 * Busca lista de cidades
 * GET /api/clients/filters/cidades
 */
export const fetchCidades = async (estado?: string) => {
    return api.get<Cidade[]>('/clients/filters/cidades', {
        params: { estado }
    });
};

/**
 * Busca clientes por nome (autocomplete) - usado no dashboard
 * GET /dashboard/search-clients
 */
export const searchClientsAPI = (query: string, carteiraId: string) => {
    return api.get<ClientSearchResult[]>('/dashboard/search-clients', {
        params: {
            q: query,
            carteiraId: carteiraId
        }
    });
};

export const createClient = async (clientData: Partial<Client>) => {
    return api.post('/clients', clientData);
};

/**
 * Atualiza um cliente existente
 * PUT /api/clients/:id
 */
export const updateClient = async (id: number, clientData: Partial<Client>) => {
    return api.put(`/clients/${id}`, clientData);
};

export interface VendedorOption {
    ID: number;
    NOME: string;
}

// 2. Adicione a Interface para Base
export interface BaseOption {
    ID: number;
    BASE: string;
}

// 3. Adicione a função de fetch
export const fetchBasesList = async () => {
    return api.get<BaseOption[]>('/clients/filters/bases-list');
};

export const fetchVendedoresList = async () => {
    return api.get<VendedorOption[]>('/clients/filters/vendedores-list');
};
